import React from 'react';
import { Alert, Badge, phone } from '@parsina/framework';
import { AlertTriangle } from 'lucide-react';
import { C, CT } from '../../../constants/theme';
import { useTranslation } from '../../../i18n/I18nProvider.jsx';
import Panel from '../../../components/common/Panel.jsx';
import { genderKey, maritalStatusKey, allergySeverityKey } from '../../../lib/patients.js';

// خلاصه — who this patient is, and what they are allergic to.
//
// Allergies are given their own panel and their own banner rather than being a
// row in the details grid: they are the one fact on this screen that changes
// what may be prescribed on the next one.

const Field = ({ label, value }) => {
  const { t } = useTranslation();
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11.5, fontWeight: 600, color: C.textMuted, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13.5, color: C.textPrimary, overflowWrap: 'anywhere' }}>
        {value || t('common.labels.none')}
      </div>
    </div>
  );
};

const Ltr = ({ children }) => (
  <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>{children}</span>
);

const SummaryTab = ({ patient }) => {
  const { t, formatNumber, formatDigits, formatDate } = useTranslation();
  const allergies = patient.allergies || [];
  const gKey = genderKey(patient.gender);
  const mKey = maritalStatusKey(patient.maritalStatus);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {allergies.length > 0 && (
        <Alert variant="warning" icon={<AlertTriangle size={16} />} title={t('patient.summary.allergyWarning')}>
          {allergies.map((a) => a.allergen).filter(Boolean).join(' · ')}
        </Alert>
      )}

      <Panel title={t('patient.summary.demographics')}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 18,
          }}
        >
          <Field label={t('common.labels.phone')} value={patient.phone ? <Ltr>{formatDigits(phone.forDisplay(patient.phone))}</Ltr> : null} />
          <Field
            label={t('common.labels.nationalId')}
            value={patient.nationalId ? <Ltr>{formatDigits(patient.nationalId)}</Ltr> : null}
          />
          <Field label={t('common.labels.fileNumber')} value={patient.fileNumber ? formatDigits(patient.fileNumber) : null} />
          {/* birth_date is free text server-side with no validator, so it is
              RENDERED, never parsed into something prettier. `age` is the
              service's own derivation and is shown beside it. */}
          <Field label={t('patient.fields.birthDate')} value={patient.birthDate ? formatDigits(patient.birthDate) : null} />
          <Field
            label={t('patient.stats.age')}
            value={patient.age != null ? t('common.units.years', { count: patient.age }) : null}
          />
          <Field label={t('patient.fields.gender')} value={gKey ? t(gKey) : patient.gender} />
          <Field label={t('patient.fields.maritalStatus')} value={mKey ? t(mKey) : patient.maritalStatus} />
          <Field label={t('patient.fields.bloodType')} value={patient.bloodType ? <Ltr>{patient.bloodType}</Ltr> : null} />
          <Field label={t('patient.fields.job')} value={patient.job} />
          <Field label={t('patient.fields.insurance')} value={patient.insurance} />
          <Field
            label={t('patient.fields.height')}
            value={patient.height ? formatNumber(patient.height) : null}
          />
          <Field
            label={t('patient.fields.createdAt')}
            value={patient.createdAt ? formatDate(patient.createdAt, 'numeric') : null}
          />
        </div>
      </Panel>

      <Panel title={t('patient.summary.allergies')}>
        {allergies.length === 0 ? (
          <div style={{ fontSize: 13, color: C.textMuted }}>{t('patient.summary.noAllergies')}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {allergies.map((a) => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: C.textPrimary }}>{a.allergen}</span>
                {/* A recognised severity is labelled; anything else renders
                    exactly as it was recorded. */}
                {a.severity && (
                  <Badge variant="outline">
                    {allergySeverityKey(a.severity) ? t(allergySeverityKey(a.severity)) : a.severity}
                  </Badge>
                )}
                {a.reaction && <span style={{ fontSize: 12.5, color: C.textSecondary }}>{a.reaction}</span>}
              </div>
            ))}
          </div>
        )}
      </Panel>

    </div>
  );
};

export default SummaryTab;
